/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    generateCacheKey,
    QueryResultSettingsProxy,
    getSelectedProductIdFromActionInput
} from '@msdyn365-commerce-modules/retail-actions';
import { getCategoryPathsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { CategoryPath, CategoryPathLookup } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

/**
 * Category path input.
 */
export class GetCurrentCategoryPathInput implements IActionInput {
    public readonly ChannelId: number;

    public readonly CatalogId: number;

    public readonly categoryPathLooksups: CategoryPathLookup[];

    public readonly apiSettings: ICommerceApiSettings;

    private readonly locale: string;

    private _requestContext: IRequestContext;

    public constructor(context: IRequestContext, categoryPathLooksups: CategoryPathLookup[]) {
        this._requestContext = context;
        this.ChannelId = context.apiSettings.channelId;
        this.CatalogId = getCatalogId(context);
        this.categoryPathLooksups = categoryPathLooksups;
        this.apiSettings = context.apiSettings;
        this.locale = context.locale;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this._requestContext.apiSettings,
            { key: 'CategoryPathProductIds', value: this.categoryPathLooksups.map(categoryPath => categoryPath.ProductId) },
            { key: 'CatalogId', value: this.CatalogId },
            { key: 'locale', value: this.locale }
        );

    public getCacheObjectType = () => 'CurrentCategoryPath';

    public dataCacheType = (): CacheType => 'request';

    /**
     * Retrieves locale to use for the current API call.
     * @returns A string which represents locale.
     */
    public getLocale = (): string => this.locale;
}

/**
 * Creates the input required to make the getCategoryPath retail api call.
 * @param inputData
 */
export const createGetCurrentCategoryPathInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    if (productId) {
        return new GetCurrentCategoryPathInput(inputData.requestContext, [{ ProductId: +productId }]);
    }
    throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
};

/**
 * Calls the Retail API and returns the category path for a product.
 * @param input
 * @param ctx
 */
export async function getCurrentCategoryPathAction(input: GetCurrentCategoryPathInput, ctx: IActionContext): Promise<CategoryPath | undefined> {
    const categoryPathResults = await getCategoryPathsAsync(
        {
            callerContext: ctx,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
        },
        input.ChannelId,
        input.CatalogId,
        input.categoryPathLooksups
    );
    const categoryPath = categoryPathResults[0].CategoryPath && categoryPathResults[0].CategoryPath[0];
    return categoryPath;
}

export const getCurrentCategoryPathActionDataAction = createObservableDataAction({
    id: 'dlvry/get-current-category-path',
    action: <IAction<CategoryPath | undefined>>getCurrentCategoryPathAction,
    input: createGetCurrentCategoryPathInput
});

export default getCurrentCategoryPathActionDataAction;
